/* import __COLOCATED_TEMPLATE__ from './editor.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import SidebarSection, {
  type SidebarSectionWireFormat,
} from 'embercom/objects/inbox/sidebar-section';
import type RoleComponent from 'embercom/models/roles/component';
import type InboxApi from 'embercom/services/inbox-api';
import type IntlService from 'embercom/services/intl';
import { SupportedSidebarSection } from 'embercom/services/inbox-sidebar-service';
import { trackedFunction } from 'ember-resources/util/function';

interface Signature {
  Args: {
    sidebarSectionComponent: RoleComponent;
  };
  Element: null;
}

export default class ConfigurableComponentsEditor extends Component<Signature> {
  supportedSidebarSection = SupportedSidebarSection;
  @tracked openSectionId: string | null = null;
  @service declare inboxApi: InboxApi;
  @service declare intl: IntlService;

  get stampText(): string {
    return this.enforced
      ? this.intl.t('settings.teammates.roles.configurable-components.enforce-mode.stamp-on')
      : this.intl.t('settings.teammates.roles.configurable-components.enforce-mode.stamp-off');
  }

  get stampColor(): 'sky' | 'gray' {
    return this.enforced ? 'sky' : 'gray';
  }

  get enforced(): boolean {
    return this.args.sidebarSectionComponent?.enforced ?? false;
  }

  get enforcedAt(): Date | null {
    return this.args.sidebarSectionComponent?.enforcedAt ?? null;
  }

  get allowAdminReorder(): boolean {
    return this.args.sidebarSectionComponent?.allowAdminReorder ?? true;
  }

  // Setter needed because item.Checkbox is a classic component that two-way binds @checked
  set allowAdminReorder(value: boolean) {
    if (this.args.sidebarSectionComponent) {
      this.args.sidebarSectionComponent.allowAdminReorder = value;
    }
  }

  get allowAdminPinUnpin(): boolean {
    return this.args.sidebarSectionComponent?.allowAdminPinUnpin ?? true;
  }

  // Setter needed because item.Checkbox is a classic component that two-way binds @checked
  set allowAdminPinUnpin(value: boolean) {
    if (this.args.sidebarSectionComponent) {
      this.args.sidebarSectionComponent.allowAdminPinUnpin = value;
    }
  }

  get availableSidebarSections(): SidebarSection[] {
    return this.availableSectionsLoader.value ?? [];
  }

  get availableSectionsLoaded(): boolean {
    return !this.availableSectionsLoader.isLoading;
  }

  get pinnedSidebarSections(): SidebarSection[] {
    let pinnedSections = this.args.sidebarSectionComponent?.configuration.map(
      (section: SidebarSectionWireFormat) => SidebarSection.deserialize(section),
    );
    return pinnedSections || [];
  }

  get unpinnedSidebarSections(): SidebarSection[] {
    let pinnedIds = this.pinnedSectionIds();
    return this.availableSidebarSections.filter((section) => {
      let id = this.mapSidebarSectionToId(section);
      return id !== undefined && !pinnedIds.has(id);
    });
  }

  @action toggleEnforced() {
    if (!this.args.sidebarSectionComponent) {
      return;
    }
    this.args.sidebarSectionComponent.enforced = !this.enforced;
  }

  @action toggleAllowAdminReorder() {
    if (!this.args.sidebarSectionComponent) {
      return;
    }
    this.args.sidebarSectionComponent.allowAdminReorder = !this.allowAdminReorder;
  }

  @action toggleAllowAdminPinUnpin() {
    if (!this.args.sidebarSectionComponent) {
      return;
    }
    this.args.sidebarSectionComponent.allowAdminPinUnpin = !this.allowAdminPinUnpin;
  }

  @action reorderSections(sections: SidebarSection[]) {
    if (!this.args.sidebarSectionComponent) {
      return;
    }
    this.args.sidebarSectionComponent.configuration = sections.map((section: SidebarSection) =>
      section.serialize(),
    );
  }

  @action onOpenSectionChange(sectionId: string) {
    this.openSectionId = sectionId;
  }

  @action
  toggleSidebarSection(section: SidebarSection) {
    if (!this.args.sidebarSectionComponent) {
      return;
    }

    let sectionId = this.mapSidebarSectionToId(section);
    let pinnedSections = (this.args.sidebarSectionComponent.configuration || []).map(
      (section: SidebarSectionWireFormat) => SidebarSection.deserialize(section),
    );
    let index = pinnedSections.findIndex(
      (pinned) => this.mapSidebarSectionToId(pinned) === sectionId,
    );
    if (index >= 0) {
      pinnedSections.splice(index, 1);
    } else {
      pinnedSections.push(section);
    }
    this.args.sidebarSectionComponent.configuration = pinnedSections.map(
      (section: SidebarSection) => section.serialize(),
    );
  }

  private pinnedSectionIds(): Set<string | number> {
    let ids = this.pinnedSidebarSections
      .map((section) => this.mapSidebarSectionToId(section))
      .filter((id): id is string | number => id !== undefined);
    return new Set(ids);
  }

  private mapSidebarSectionToId(section: SidebarSection): number | string | undefined {
    return section.intercomModuleIdentifier || section.inboxAppId;
  }

  private availableSectionsLoader = trackedFunction(this, async () => {
    return await this.inboxApi.listAvailableSidebarSections();
  });
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Settings::Teammates::Roles::ConfigurableComponents::Editor': typeof ConfigurableComponentsEditor;
    'settings/teammates/roles/configurable-components/editor': typeof ConfigurableComponentsEditor;
  }
}
